// Drawola Studio — template preview images (Phase 3). Fork-only.
//
// There are no static image assets for the templates: a card's preview is the
// template's own scene, rendered once through the same `generateThumbnail()`
// the Studio already uses for real draws (storage/thumbnails.ts). That keeps
// a preview honest by construction — it cannot drift from what clicking the
// card actually produces.
//
// Cost control:
//  - Rendering happens in a `useEffect`, one template per idle slice, so the
//    Studio's first paint is never blocked; cards show a neutral placeholder
//    until their image lands.
//  - Results are memoised in a MODULE-level cache, so going back and forth
//    between the Studio and the editor re-renders nothing. Template scenes are
//    fixed, so the cache never needs invalidating for the life of the tab.
//  - A failed or empty render is cached as `null` and is never retried, for
//    the same reason the thumbnail backfill does not retry.

import { useEffect, useState } from "react";

import { generateThumbnail } from "../../storage/thumbnails";

import type { StudioTemplate } from "./types";

/** template id -> data URL, or `null` when the render did not produce one. */
const previewCache = new Map<string, string | null>();

/** Cap on how long the browser may defer a render step. */
const IDLE_TIMEOUT_MS = 2_000;
/** `requestIdleCallback` fallback delay. */
const IDLE_FALLBACK_MS = 200;

export type TemplatePreviews = Record<string, string | null>;

/**
 * Preview data URLs keyed by template id.
 *
 * A missing key means "still generating" — render the placeholder. A `null`
 * value means "we tried and there is no image" — also the placeholder, but it
 * will not change.
 */
export const useTemplatePreviews = (
  templates: readonly StudioTemplate[],
  enabled: boolean,
): TemplatePreviews => {
  const [previews, setPreviews] = useState<TemplatePreviews>(() =>
    Object.fromEntries(previewCache),
  );

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const queue = templates.filter(
      (template) => !previewCache.has(template.id),
    );
    if (!queue.length) {
      return undefined;
    }

    let cancelled = false;
    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const schedule = (step: () => void) => {
      if (cancelled) {
        return;
      }
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(
          () => {
            idleId = null;
            step();
          },
          { timeout: IDLE_TIMEOUT_MS },
        );
      } else {
        timeoutId = window.setTimeout(() => {
          timeoutId = null;
          step();
        }, IDLE_FALLBACK_MS);
      }
    };

    const step = async () => {
      const template = queue.shift();
      if (!template) {
        return;
      }
      let preview: string | null = null;
      try {
        const scene = template.build();
        preview = await generateThumbnail(
          scene.elements,
          scene.appState,
          undefined,
        );
      } catch (error) {
        // A preview is a nicety — same policy as storage/thumbnails.ts.
        // eslint-disable-next-line no-console
        console.warn(
          `[drawOla] template preview failed for "${template.id}"`,
          error,
        );
        preview = null;
      }
      // Cache even when the effect has been torn down: the work is done and
      // the result is not tied to this mount.
      previewCache.set(template.id, preview);
      if (cancelled) {
        return;
      }
      setPreviews((current) => ({ ...current, [template.id]: preview }));
      schedule(() => {
        void step();
      });
    };

    schedule(() => {
      void step();
    });

    return () => {
      cancelled = true;
      if (idleId !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      idleId = null;
      timeoutId = null;
    };
  }, [enabled, templates]);

  return previews;
};
